import { useEffect, useRef, useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { Info } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { TicketThread } from "@/components/domain/ticket-thread"
import { TicketComposer } from "@/components/domain/ticket-composer"
import { TicketMetaPanel, TicketActionFooter } from "@/components/domain/ticket-meta-panel"
import { Sheet, SheetHeader, SheetBody } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import type { Ticket } from "@/types/ticket"
import type { SharedProps } from "@/types/inertia"

type Props = {
  ticket: Ticket
  ticket_stream: string
}

export default function TicketsShow({ ticket, ticket_stream }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash

  // Live thread + status updates. New messages from the customer or AI
  // flow in via AgentMessage's broadcasts_to; status flips (resolved /
  // re-escalated) flow in via AgentConvo's broadcasts_to. Same stream.
  useInertiaCable(ticket_stream, {
    only: ["ticket"],
    debounce: 250,
  })

  // Auto-scroll the thread to the bottom on first paint AND whenever the
  // message count changes (new message arrived). preserveScroll on the
  // composer keeps the user's view stable when THEY send.
  const threadRef = useRef<HTMLDivElement | null>(null)
  const messageCount = ticket.messages?.length ?? 0
  useEffect(() => {
    const el = threadRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messageCount])

  // Mobile-only: a "Details" button in the page header opens the meta
  // panel as a bottom sheet. Desktop renders the meta panel inline as
  // the right pane and never opens this sheet.
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <AppShell>
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-app-muted">
              <Link href="/tickets" className="hover:text-app-text">Tickets</Link>
              <span className="px-1.5 text-app-subtle">›</span>
              <span>Ticket {ticketNumberLabel(ticket)}</span>
            </div>
            <h1 className="text-2xl font-semibold text-app-text sm:text-3xl">{ticket.subject}</h1>
          </div>
          {/* Mobile-only: open the meta panel as a sheet. Desktop has it
              already visible in the right pane. */}
          <Button
            size="md"
            variant="ghost"
            className="lg:hidden"
            onClick={() => setDetailsOpen(true)}
          >
            <Info size={16} strokeWidth={2} className="mr-1.5" /> Details
          </Button>
        </div>
      </div>

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      {/* Conversation pane + (desktop-only) meta panel.
          Mobile: column stack (thread + composer); meta panel hidden,
                  reachable via the "Details" sheet.
          Desktop: 50/50 row split with the right pane taking the meta
                   panel + a floating action bar.
          The container caps at calc(100vh-220px) on lg+ so the inner
          scroll regions (thread + meta tabs) sit inside a fixed shell —
          on mobile we let it grow with the page so phone users can
          scroll the whole thing naturally. */}
      <section className="flex flex-col overflow-hidden rounded-card border border-border bg-app-panel lg:h-[calc(100vh-220px)] lg:flex-row">
        <div className="flex min-h-0 flex-col lg:w-1/2">
          <div ref={threadRef} className="min-h-[40vh] flex-1 overflow-y-auto lg:min-h-0">
            <TicketThread messages={ticket.messages ?? []} />
          </div>
          <TicketComposer
            ticketId={ticket.id}
            disabled={ticket.status === "closed" || ticket.status === "pending_feedback"}
          />
        </div>
        <aside className="relative hidden flex-col border-l border-border bg-app-panel lg:flex lg:w-1/2">
          <TicketMetaPanel ticket={ticket} />
          {/* Desktop floating action bar — hovers over the scrolling
              meta-panel content. Position: absolute inside the aside. */}
          <div className="pointer-events-none absolute inset-x-4 bottom-4">
            <TicketActionFooter ticket={ticket} className="pointer-events-auto" />
          </div>
        </aside>
      </section>

      {/* Mobile sticky action footer — sits below the conversation pane,
          always visible at the bottom of the viewport so the close-paths
          don't require entering the Details sheet. The negative margins
          align the footer with the viewport edge instead of the inner
          AppShell padding. */}
      <div className="sticky bottom-0 -mx-4 mt-2 border-t border-border bg-app-panel/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden">
        <TicketActionFooter ticket={ticket} className="border-0 bg-transparent p-0 shadow-none" />
      </div>

      {/* Mobile-only details sheet. Hosts the meta panel below `lg:`. */}
      <Sheet
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        side="bottom"
      >
        <SheetHeader title="Ticket details" onClose={() => setDetailsOpen(false)} />
        <SheetBody className="p-0">
          <TicketMetaPanel ticket={ticket} />
        </SheetBody>
      </Sheet>
    </AppShell>
  )
}

function ticketNumberLabel(ticket: Ticket) {
  if (ticket.displayTicketNumber) return ticket.displayTicketNumber
  if (ticket.supportProvider === "zendesk" && ticket.externalId) return `ZD-${ticket.externalId}`
  return ticket.displayTicketNumber || (ticket.ticketNumber != null ? `TK-${ticket.ticketNumber}` : "—")
}
